import { useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { isDarkColor } from '../utils/colorUtils';
import usersService from '../services/usersService';
import { themeIdToType } from '../constants/ThemeType';
import AppSnackbar from './ui/AppSnackbar';
import { useSnackbar } from '../hooks/useSnackbar';

/**
 * Componente seletor de tema
 * Exibe um dropdown para trocar entre os temas disponíveis
 * Persiste a escolha no backend via API
 */
export default function ThemeSelector() {
  const { themeId, availableThemes, changeTheme, loading, getColor } = useThemeCustom();
  const [localLoading, setLocalLoading] = useState(false);
  const { snackbar: successSnackbar, showSuccess, hideSnackbar } = useSnackbar();
  const [error, setError] = useState(null);

  // Determinar se drawer é escuro
  const drawerBackgroundColor = getColor('backgroundDrawer');
  const isDrawerDark = isDarkColor(drawerBackgroundColor);

  const handleChange = async (event) => {
    const newThemeId = event.target.value;
    setLocalLoading(true);
    setError(null);
    
    try {
      // 1. Persistir no backend PRIMEIRO
      const themeType = themeIdToType(newThemeId);
      console.log('[ThemeSelector] Salvando tema no backend:', { themeId: newThemeId, themeType });
      await usersService.updateTheme(themeType);
      console.log('[ThemeSelector] Tema salvo no backend com sucesso');
      
      // 2. Aplicar tema localmente
      await changeTheme(newThemeId);

      // 3. Mostrar feedback de sucesso
      showSuccess('Tema atualizado com sucesso!');
      console.log('[ThemeSelector] Tema aplicado com sucesso');
    } catch (err) {
      console.error('[ThemeSelector] Erro ao alterar tema:', err);
      setError('Erro ao salvar tema. Tente novamente.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 150,
        }}
      >
        <PaletteIcon sx={{ 
          color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', 
          fontSize: 20 
        }} />
        <FormControl size="small" fullWidth>
          <Select
            value={themeId}
            onChange={handleChange}
            disabled={loading || localLoading}
            sx={{
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '0.875rem',
                color: isDrawerDark ? '#FFFFFF' : 'inherit',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDrawerDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDrawerDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.87)',
              },
              bgcolor: isDrawerDark ? 'rgba(255,255,255,0.08)' : 'transparent',
              '& .MuiSvgIcon-root': {
                color: isDrawerDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.54)',
              },
            }}
            startAdornment={
              (loading || localLoading) && (
                <CircularProgress size={16} sx={{ mr: 1, color: isDrawerDark ? '#FFFFFF' : 'inherit' }} />
              )
            }
          >
            {availableThemes.map((theme) => (
              <MenuItem key={theme.id} value={theme.id}>
                <Typography variant="body2">{theme.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Snackbar de sucesso */}
      <AppSnackbar snackbar={successSnackbar} onClose={hideSnackbar} autoHideDuration={3000} />

      {/* Snackbar de erro */}
      {error && <Alert onClose={handleCloseError} severity="error" variant="filled" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}>
        {error}
      </Alert>}
    </>
  );
}
